import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { MessageSquare, Bug, HelpCircle, Lightbulb, AlertTriangle, Loader2, Send } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscriptionStore } from '@/store/useSubscriptionStore';
import { useDailyUsage } from '@/hooks/useDailyUsage';
import { submitFeedback, FeedbackSubmission } from '@/lib/dailyUsage';
import { UsageBadge } from './UsageBadge';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const FEEDBACK_TAGS: { value: FeedbackSubmission['tag']; label: string; icon: React.ElementType; color: string }[] = [
  { value: 'report', label: 'Report', icon: AlertTriangle, color: 'text-destructive' },
  { value: 'issue', label: 'Issue', icon: Bug, color: 'text-amber-500' },
  { value: 'doubt', label: 'Doubt', icon: HelpCircle, color: 'text-blue-500' },
  { value: 'suggestion', label: 'Suggestion', icon: Lightbulb, color: 'text-emerald-500' },
];

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpgrade?: () => void;
}

export function FeedbackDialog({ open, onOpenChange, onUpgrade }: FeedbackDialogProps) {
  const { user } = useAuth();
  const { isPremium, limits } = useSubscriptionStore();
  const { getRemainingFeedback, trackFeedback } = useDailyUsage();
  const [selectedTag, setSelectedTag] = useState<FeedbackSubmission['tag']>('suggestion');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const remainingFeedback = getRemainingFeedback();
  const premium = isPremium();

  const handleSubmit = async () => {
    if (!user || !message.trim()) return;

    const canSubmit = await trackFeedback(onUpgrade);
    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      const result = await submitFeedback(
        user.uid,
        user.displayName || 'Anonymous',
        user.email || '',
        premium,
        selectedTag,
        message.trim()
      );

      if (result.success) {
        toast.success('Feedback submitted successfully!');
        setMessage('');
        setSelectedTag('suggestion');
        onOpenChange(false);
      } else {
        toast.error(result.error || 'Failed to submit feedback');
      }
    } catch (error) {
      toast.error('Failed to submit feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            Send Feedback
          </DialogTitle>
          <div className="flex items-center justify-between gap-2">
            <DialogDescription className="mb-0">
              Help us improve ApplyWise
            </DialogDescription>
            <UsageBadge
              used={premium ? limits.maxFeedbackPerDay - remainingFeedback : limits.maxFeedbackPerDay - remainingFeedback}
              limit={limits.maxFeedbackPerDay}
              label="today"
              resetType="daily"
              isPremium={premium}
              onClick={onUpgrade}
              compact
            />
          </div>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Tag Selection */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Category</Label>
            <div className="grid grid-cols-2 gap-2">
              {FEEDBACK_TAGS.map(({ value, label, icon: Icon, color }) => (
                <button
                  key={value}
                  onClick={() => setSelectedTag(value)}
                  className={cn(
                    "flex items-center gap-2 p-2.5 rounded-lg border text-sm font-medium transition-all",
                    selectedTag === value
                      ? "border-primary bg-primary/5 text-primary"
                      : "border-border bg-background hover:bg-muted/50 text-muted-foreground"
                  )}
                >
                  <Icon className={cn("h-4 w-4", selectedTag === value ? "text-primary" : color)} />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Message</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Describe your feedback, issue, or suggestion..."
              className="min-h-[100px] resize-none"
              maxLength={1000}
            />
            <p className="text-xs text-muted-foreground text-right">{message.length}/1000</p>
          </div>

          {/* Submit */}
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !message.trim() || !user}
            className="w-full gap-2"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
